import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { MapAdapter } from '../maps/MapAdapter';
import { createMapAdapter } from '../maps/createAdapter';
import { describeRoutePlanningError } from '../maps/tripMapPaths';
import type { LatLng, MapMarker, MapSettings, RouteResult } from '../maps/types';

export interface TripMapViewHandle {
  panTo: (position: LatLng) => void;
  updateMarker: (id: string, position: LatLng) => void;
  getDrivingRoute: (from: LatLng, to: LatLng | string) => Promise<RouteResult | null>;
  reverseGeocode: (position: LatLng) => Promise<string>;
  getAdapter: () => MapAdapter | null;
}

export interface TripMapViewProps {
  center: LatLng;
  zoom?: number;
  markers?: MapMarker[];
  /** Provider-planned route — used when `track` has fewer than 2 points. */
  route?: { from: LatLng; to: LatLng };
  /** Actual GPS trajectory from location_logs. Takes priority over `route`. */
  track?: LatLng[];
  mapSettings?: MapSettings;
  className?: string;
  style?: CSSProperties;
  onReady?: () => void;
  onError?: (message: string) => void;
  /** Fired when provider route planning fails (planned mode only). */
  onRoutePlanningError?: (message: string) => void;
  onRoutePlanningSuccess?: () => void;
}

async function applyMapPaths(
  adapter: MapAdapter,
  track: LatLng[] | undefined,
  route: { from: LatLng; to: LatLng } | undefined,
  center: LatLng,
  markers: MapMarker[],
  onRoutePlanningError?: (message: string) => void,
  onRoutePlanningSuccess?: () => void
): Promise<void> {
  const markerPoints = markers.map((m) => m.position);

  if (track && track.length >= 2) {
    await adapter.drawTrack(track);
    adapter.fitPoints([...track, ...markerPoints]);
    return;
  }

  adapter.clearTrack();
  if (route) {
    const result = await adapter.drawRoute(route.from, route.to);
    if (!result.ok) {
      onRoutePlanningError?.(
        describeRoutePlanningError(adapter.provider, result.error ?? 'UNKNOWN')
      );
    } else {
      onRoutePlanningSuccess?.();
    }
    adapter.fitPoints([route.from, route.to, center, ...markerPoints]);
    return;
  }

  adapter.clearRoute();
  if (markerPoints.length > 0) {
    adapter.fitPoints(markerPoints);
  }
}

export const TripMapView = forwardRef<TripMapViewHandle, TripMapViewProps>(function TripMapView(
  {
    center,
    zoom = 13,
    markers = [],
    route,
    track,
    mapSettings,
    className,
    style,
    onReady,
    onError,
    onRoutePlanningError,
    onRoutePlanningSuccess,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<MapAdapter | null>(null);
  const [loading, setLoading] = useState(true);
  const mapReadyRef = useRef(false);

  const syncPaths = useCallback(async () => {
    if (!adapterRef.current || !mapReadyRef.current) return;
    await applyMapPaths(
      adapterRef.current,
      track,
      route,
      center,
      markers,
      onRoutePlanningError,
      onRoutePlanningSuccess
    );
  }, [
    track,
    route?.from.lat,
    route?.from.lng,
    route?.to.lat,
    route?.to.lng,
    center.lat,
    center.lng,
    markers,
    onRoutePlanningError,
    onRoutePlanningSuccess,
  ]);

  useImperativeHandle(ref, () => ({
    panTo: (position) => adapterRef.current?.panTo(position),
    updateMarker: (id, position) => adapterRef.current?.updateMarker(id, position),
    getDrivingRoute: (from, to) =>
      adapterRef.current?.getDrivingRoute(from, to) ?? Promise.resolve(null),
    reverseGeocode: (position) =>
      adapterRef.current?.reverseGeocode(position) ??
      Promise.resolve(`${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`),
    getAdapter: () => adapterRef.current,
  }));

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    mapReadyRef.current = false;
    setLoading(true);
    (async () => {
      try {
        adapterRef.current?.destroy();
        const adapter = await createMapAdapter(mapSettings);
        if (cancelled) {
          adapter.destroy();
          return;
        }
        await adapter.init(container, { center, zoom });
        adapter.setMarkers(markers);
        await applyMapPaths(
          adapter,
          track,
          route,
          center,
          markers,
          onRoutePlanningError,
          onRoutePlanningSuccess
        );
        adapterRef.current = adapter;
        mapReadyRef.current = true;
        setLoading(false);
        onReady?.();
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load map';
          onError?.(message);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      mapReadyRef.current = false;
      adapterRef.current?.destroy();
      adapterRef.current = null;
    };
  }, [
    center.lat,
    center.lng,
    zoom,
    mapSettings?.provider,
    mapSettings?.googleApiKey,
  ]);

  useEffect(() => {
    adapterRef.current?.setMarkers(markers);
  }, [markers]);

  useEffect(() => {
    void syncPaths();
  }, [syncPaths]);

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%', ...style }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(244,246,248,0.85)',
            fontSize: 14,
            color: '#6B7280',
          }}
        >
          Loading map...
        </div>
      )}
    </div>
  );
});
